// =============================================================================
// Quote Types for Quotes Page
// Data source: PostgreSQL via GET /v4/quotes API (V4)
// Legacy data source: MongoDB via GET /v4/legacy/quotes API (V3)
// =============================================================================

import { taxForCategory, type TaxCategory } from '@atek/ca-tax';

/**
 * Quote status values
 * Note: 'expired' is computed based on expiration_date
 */
export type QuoteStatus =
  | 'preparation'
  | 'sent'
  | 'waiting-po'
  | 'approved'
  | 'confirmed'
  | 'inprogress'
  | 'invoice-ready'
  | 'partially-invoiced'
  | 'invoiced'
  | 'cancelled'
  | 'rejected'
  | 'expired';

/**
 * Deal type options
 */
export type DealType = 'newbusiness' | 'existingbusiness' | 'recurring' | 'rfp';

/**
 * Payment terms options
 */
export type PaymentTerms = 'on-order' | 'net-0' | 'net-30' | 'net-45' | 'net-60';

/**
 * Tax category options — sourced from @atek/ca-tax (the full Canadian set incl. HST).
 */
export type { TaxCategory };

/**
 * Currency options
 */
export type Currency = 'CAD' | 'USD' | 'EUR';

/**
 * Language options
 */
export type Language = 'en' | 'fr';

/**
 * Operations triage priority. Null/undefined = never set (legacy quotes) —
 * the operations Kanban shows no badge in that case.
 */
export type QuotePriority = 'urgent' | 'normal' | 'low';

/**
 * V4 Quote from PostgreSQL
 */
export interface Quote {
  id: string;
  quoteNumber: string;
  projectName: string | null;
  name: string | null;
  description: string | null;
  status: QuoteStatus;
  priority?: QuotePriority | null;

  // Dates
  createdDate: string | null;
  expirationDate: string | null;
  sentDate: string | null;
  acceptedDate: string | null;
  estimatedDelivery: string | null;
  createdAt: string | null;
  updatedAt: string | null;

  // Financial
  subtotal: string;
  discount: string;
  taxAmount: string;
  total: string;
  currency: Currency;
  taxCategory: TaxCategory | null;

  // Quote-owned operations board state
  operationsStage?: string | null;
  operationsPosition?: number | null;
  operationsMilestones?: QuoteOperationsMilestone[];
  operationsNotes?: string | null;
  operationsInternalNotes?: QuoteOperationsInternalNote[];

  // Managers (MongoDB ObjectIds)
  salesManagerId: string | null;
  contractualManagerId: string | null;
  // Enriched manager info
  salesManagerName: string | null;
  contractualManagerName: string | null;
  contractualManagerEmail: string | null;
  contractualManagerPhone: string | null;

  // External links
  hubspotDealname: string | null;
  hubspotLink: string | null;
  gdriveLink: string | null;

  // Account info (joined)
  accountId: string;
  accountName: string | null;
  accountNumber: string | null;

  // MongoDB references for backward compatibility
  mongoQuoteId: string | null;

  // Contract reference
  contractId: string | null;

  // Additional fields
  poNumber: string | null;
  shippingAddress: string | null;

  createdBy: string | null;

  // V4 line items, used when a quote has no MongoDB backing document
  lineItems?: QuoteLineItem[];

  // Fields available from V3 but not yet in V4 API — optional until V4 enhanced
  projectNumber?: string | null;
  date?: string | null;
  dealType?: string | null;
  nextStep?: string | null;
  nextStepDeadline?: string | null;
  invoiceNumbers?: string[] | null;
  trackingNumbers?: string[] | null;
  billingAddress?: string | null;
  poFile?: string | null;
  contractName?: string | null;
  contractNumber?: string | null;
  ticketName?: string | null;
  subscriptionName?: string | null;
  subscriptionNumber?: string | null;
}

export interface QuoteOperationsMilestone {
  id?: string;
  key: string;
  displayName?: string;
  label?: string;
  sortOrder?: number;
  completed?: boolean;
  completedAt?: string | null;
  completedBy?: string | null;
  completedByName?: string | null;
  notes?: string | null;
}

export interface QuoteOperationsInternalNote {
  id?: string;
  content?: string;
  createdAt?: string;
  createdBy?: string;
  createdByName?: string;
}

export interface QuoteLineItem {
  id: string;
  quoteId?: string;
  sku?: string | null;
  name?: string | null;
  description?: string | null;
  quantity: string | number;
  unitPrice: string | number;
  discount?: string | number | null;
  total: string | number;
  sortOrder?: number | null;
  itemNumber?: number | null;
  subtotalGroup?: string | null;
  subtotalLabel?: string | null;
  isNote?: boolean | null;
}

export interface QuoteOperationsAuditLog {
  id: string;
  quoteId: string;
  timestamp: string;
  action: string;
  details?: Record<string, unknown>;
  performedBy?: string | null;
  performedByName?: string | null;
}

export interface QuoteOperationsAuditLogsResponse {
  logs: QuoteOperationsAuditLog[];
  pageNumber: number;
  pageLength: number;
  total: number;
}

/**
 * Quote status statistics
 */
export interface QuoteStatusStats {
  status: QuoteStatus | 'total';
  quantity: number;
  amount: number;
}

/**
 * Wrapper for quote stats result including accuracy metadata.
 * Client-side aggregation may only process a subset of quotes.
 */
export interface QuoteStatsResult {
  stats: QuoteStatusStats[];
  /** True when the dataset was too large to aggregate client-side (>1000 quotes) */
  isApproximate: boolean;
  /** Number of quotes actually processed */
  processed: number;
  /** Total number of quotes matching the filters */
  total: number;
}

/**
 * Filters for querying quotes
 */
export interface QuotesFilters {
  search?: string;
  poNumber?: string;
  /** Dedicated client account-number filter (# Compte column) — matched against
   * accounts.accountNumber only, never the global multi-field search. */
  accountNumber?: string;
  accountId?: string;
  accountIdIn?: string[];
  status?: QuoteStatus;
  status_in?: QuoteStatus[];
  salesManagerId?: string;
  salesManagerIdIn?: string[];
  sortKey?: 'quoteNumber' | 'createdAt' | 'projectName' | 'total' | 'status' | 'acceptedDate' | 'accountName' | 'date' | 'projectNumber' | 'dealType' | 'nextStepDeadline';
  sortDirection?: 1 | -1;
  pageNumber?: number;
  pageLength?: number;
}

/**
 * API response for GET /v4/quotes
 */
export interface QuotesResponse {
  quotes: Quote[];
  pageNumber: number;
  pageLength: number;
  total: number;
}

/**
 * Organisation for dropdown filter (customer/account)
 */
export interface Organisation {
  _id: string;
  name: string;
  /**
   * The V2/PostgreSQL account number, verbatim (`accounts.account_number`).
   * This is the ONLY field that carries the `-NN` sub-account suffix
   * (e.g. `"0013-07"`), which is why it is a string. Prefer it over `org_num`
   * for display and for matching an account by number.
   */
  accountNumber?: string;
  /**
   * The legacy V1/MongoDB organisation number. Numeric, 4 digits, and has no
   * sub-account notion — every sub-account leaves this `undefined`.
   */
  org_num?: number;
  enabled?: boolean;
  mongoOrgId?: string;
  /** The account's assigned Account Manager id, used to pre-fill the AM on the ERP New Quote flow. */
  salesManagerId?: string | null;
}

/**
 * Account for dropdown filter (V4 format)
 */
export interface Account {
  id: string;
  name: string;
  accountNumber: string | null;
}

export interface QuoteAccountFilterOption {
  _id: string;
  name: string;
  accountNumber: string | null;
}

export interface QuoteAccountFilterPage {
  accounts: QuoteAccountFilterOption[];
  pageNumber: number;
  pageLength: number;
  total: number;
}

// =============================================================================
// V3 Types (MongoDB - for form mutations)
// =============================================================================

/**
 * Populated organisation reference from V3
 */
export interface PopulatedOrganisation {
  _id: string;
  name: string;
  org_num?: number;
  enabled?: boolean;
}

/**
 * Populated user reference from V3
 */
export interface PopulatedUser {
  _id: string;
  firstname?: string;
  lastname?: string;
  email?: string;
  phone?: string;
}

/**
 * Populated contract reference from V3
 */
export interface PopulatedContract {
  _id: string;
  name: string;
  contract_number?: string;
  customer?: string | PopulatedOrganisation;
}

/**
 * Populated site reference from V3
 */
export interface PopulatedSite {
  _id: string;
  name: string;
  address?: string;
  city?: string;
  province?: string;
  postal_code?: string;
  country?: string;
  tags?: string[];
  email?: string;
}

/**
 * V3 Quote SKU line item
 */
export interface V3QuoteSku {
  _id?: string;
  sku?: string;
  sku_id?: string;
  name?: string;
  description?: string;
  quantity: number;
  unit_price: number;
  discount?: number;
  total?: number;
  subtotal_id?: string;
}

/**
 * V3 Quote subtotal section
 * Schema uses `displayName` as the label and `_id` derived from it (lowercase, spaces→dashes)
 */
export interface V3QuoteSubtotal {
  _id?: string;
  displayName: string;
  total?: number;
}

/**
 * V3 Quote note
 */
export interface V3QuoteNote {
  _id: string;
  content: string;
  editedContent?: string;
  user: string;
  user_name: string;
  date: string;
  editedDate?: string;
  deleted?: boolean;
}

/**
 * Pricelist from MongoDB
 */
export interface Pricelist {
  _id: string;
  name: string;
  deleted?: boolean;
  skus?: Array<{ sku: string; unit_price: number }>;
}

/**
 * Subscription from MongoDB
 */
export interface Subscription {
  _id: string;
  sub_number?: string;
  name: string;
  customer?: string;
  contractual_manager?: string;
  project_name?: string;
  description?: string;
  internal_notes?: string;
  gdrive_link?: string;
  billing_site?: string;
  billing_address?: string;
  shipping_addresses?: Array<{ site?: string; address?: string }>;
  probability?: number;
  language?: Language;
  currency?: Currency;
  tax_category?: TaxCategory;
  pricelist?: string;
  skus?: V3QuoteSku[];
  subtotals?: V3QuoteSubtotal[];
  deleted?: boolean;
}

/**
 * Invoice summary (for invoiced qty calculation and invoice numbers display)
 */
export interface InvoiceSummary {
  _id: string;
  invoice_number: string;
  quote_number?: string;
  status: string;
  skus?: Array<{
    sku: string;
    unit_price: number;
    discount?: number;
    quantity: number;
    ref_id?: string;
  }>;
}

/**
 * Sensor as loaded for a quote's sensors section.
 * Populated with stream, location, and installationqualification from V3 API.
 */
export interface QuoteSensor {
  _id: string;
  customid?: string;
  name: string;
  location?: { _id: string; name: string };
  stream?: {
    _id: string;
    name: string;
    calibration?: {
      calibrationrecord?: string;
      cert_number?: string;
      date?: string;
      due?: string;
    };
  };
  installationqualification?: {
    _id: string;
    report_number?: string;
    date?: string;
  };
  status?: number;
  /** User Group ("groupe utilisateur") the sensor belongs to — name as returned by the V4 picker. */
  usergroup?: string;
  /** Sensor Groups ("groupes capteur") the sensor belongs to (a sensor can be in several). */
  sensorgroups?: { id: string; name: string }[];
  invoices: string[];
  quotes: string[];
  // New structured associations (from junction tables, optional)
  invoiceAssociations?: { id: string; invoiceNumber: string; status: string; invoiceDate: string | null; total: string }[];
  quoteAssociations?: { id: string; quoteNumber: string; status: string; total: string }[];
}

/** Parameters for paginated sensor selection dialog */
export interface SensorSelectParams {
  pageNumber?: number;
  pageLength?: number;
  sortKey?: string;
  sortDirection?: 1 | -1;
  search?: string;
  nameSearch?: string;
  streamSearch?: string;
  locationSearch?: string;
  status_in?: number[];
  /** Structured filters (#1177) — PG uuids from /v4/accounts/:id/sensor-filter-options */
  usergroupId?: string;
  sensorgroupId?: string;
  siteId?: string;
}

/** One selectable value of a sensor-filter facet */
export interface SensorFilterOption {
  id: string;
  name: string;
}

/** Filter options for the account sensor-select dialog (#1177) */
export interface AccountSensorFilterOptions {
  usergroups: SensorFilterOption[];
  sensorgroups: SensorFilterOption[];
  sites: SensorFilterOption[];
}

/** Response from V3 sensors endpoint */
export interface SensorsResponse {
  data: QuoteSensor[];
  total: number;
}

/**
 * V3 Quote from MongoDB (full form data)
 */
export interface V3Quote {
  _id: string;
  /**
   * PostgreSQL UUID for V4/PG-native quotes, distinct from `_id` (the Mongo
   * shadow ObjectId written by the legacy dual-write bridge). Populated by
   * `fromV4QuoteToV3Shape`; undefined for pure legacy-Mongo quotes. Route
   * follow-up updates by this id so they hit the guard-free V4 `PUT
   * /v4/quotes/:id` rather than the V3 legacy passthrough (which returns 423
   * on a quote flagged `migrated_to_pg`).
   */
  id?: string;
  quote_number: string;
  status: QuoteStatus;
  dealtype?: DealType;
  probability?: number;

  // Customer & references
  customer?: string | PopulatedOrganisation;
  contract?: string | PopulatedContract;
  ticket?: string;
  subscription?: string;

  // Managers
  sales_manager?: string | PopulatedUser;
  contractual_manager?: string | PopulatedUser;

  // Project info
  project_name?: string;
  project_number?: string;
  description?: string;
  comment?: string;
  internal_notes?: string;

  // Dates
  date?: string;
  expiration_date?: string;
  estimated_delivery?: string;
  accepted_at?: string;
  next_step_deadline?: string;
  created_at?: string;
  updated_at?: string;

  // Financial
  currency: Currency;
  language: Language;
  tax_category?: TaxCategory;
  payment_terms?: PaymentTerms;
  pricelist?: string;
  total?: number;

  // SKUs
  skus?: V3QuoteSku[];
  subtotals?: V3QuoteSubtotal[];

  // Addresses
  billing_site?: string | PopulatedSite;
  billing_address?: string;
  shipping_addresses?: Array<{
    site?: string | PopulatedSite;
    name?: string;
    address?: string;
  }>;

  // Files & links
  po_number?: string;
  po_file?: string;
  hubspot_link?: string;
  hubspot_dealname?: string;
  gdrive_link?: string;
  tracking_numbers?: string[];

  // Next step
  next_step?: string;

  // Invoice tracking
  invoice_numbers?: string[];
  quote_version?: number;

  // Notes & changelog
  notes?: V3QuoteNote[];

  // Metadata
  deleted?: boolean;
  created_by?: string;
  migrated_to_pg?: boolean;
}

/**
 * Input for creating a new quote
 */
export interface CreateQuoteInput extends Omit<UpdateQuoteInput, 'status'> {
  customer: string;
  project_name: string;
  sales_manager: string;
  status?: QuoteStatus;
  /**
   * PG account UUID. Required when the caller routes through V4-native
   * `createQuote` (which POSTs to `/v4/quotes`). The legacy V3 passthrough
   * tolerates this field being absent because it resolves the account via
   * the Mongo `customer` field on the Mongo side; the V4 endpoint cannot.
   */
  accountId?: string;
  /**
   * PG subscription UUID for V4 create. Captured from the Contract dropdown
   * value (formData.contract holds the PG UUID) before it is rewritten to the
   * MongoDB ObjectId for the legacy V3 payload in buildQuoteSubmitData.
   * `toCreateQuoteV4Payload` prefers this field over the legacy `subscription`
   * field to ensure the V4 FK resolves to the correct PG row.
   */
  subscriptionId?: string;
}

/**
 * Input for the PG-native header-only quote create for a brand-new customer.
 * Auto-creates a PG account server-side (flag-gated behind
 * `FEATURE_AUTO_CREATE_ACCOUNT_ON_QUOTE`). No `accountId` — the account doesn't
 * exist yet.
 */
export interface CreateQuoteForNewCustomerInput {
  newCustomer: { name: string };
  projectName?: string;
  description?: string;
  status?: QuoteStatus;
  salesManagerId?: string;
}

/**
 * Raw PostgreSQL `quotes` row returned by the new-customer create endpoint
 * (`POST /v4/quotes` with a `newCustomer` body, response `{ quote }`). This
 * is the DAO's insert-returning row for a header-only quote (no line items)
 * — it does NOT carry the joined/enriched fields the list/detail `Quote`
 * shape has (accountName, accountNumber, salesManagerName,
 * contractualManager*, etc.). Deliberately a separate, smaller type instead
 * of force-casting to `Quote`.
 */
export interface NewCustomerQuoteRow {
  id: string;
  quoteNumber: string;
  accountId: string;
  mongoQuoteId?: string | null;
  name?: string | null;
  projectName?: string | null;
  description?: string | null;
  status?: QuoteStatus;
  createdDate?: string | null;
  subtotal?: string;
  discount?: string;
  taxAmount?: string;
  total?: string;
  currency?: Currency;
  salesManagerId?: string | null;
  createdBy?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

/**
 * Input for updating a quote
 */
export interface UpdateQuoteInput {
  status?: QuoteStatus;
  priority?: QuotePriority | null;
  dealtype?: DealType;
  probability?: number;
  quote_number?: string;
  quote_version?: number;
  customer?: string;
  contract?: string;
  ticket?: string;
  subscription?: string;
  sales_manager?: string;
  contractual_manager?: string;
  project_name?: string;
  project_number?: string;
  description?: string;
  comment?: string;
  internal_notes?: string;
  date?: string;
  expiration_date?: string;
  estimated_delivery?: string;
  accepted_at?: string;
  next_step?: string;
  next_step_deadline?: string;
  currency?: Currency;
  language?: Language;
  tax_category?: TaxCategory;
  payment_terms?: PaymentTerms;
  pricelist?: string;
  skus?: V3QuoteSku[];
  subtotals?: V3QuoteSubtotal[];
  total?: number;
  billing_site?: string;
  billing_address?: string;
  shipping_addresses?: Array<{
    site?: string;
    name?: string;
    address?: string;
  }>;
  po_number?: string;
  /** File id from the legacy files store; null detaches the current PO file. */
  po_file?: string | null;
  hubspot_link?: string;
  gdrive_link?: string;
  tracking_numbers?: string[];
  invoice_numbers?: string[];
}

/**
 * Input for adding a note to a quote
 */
export interface AddQuoteNoteInput {
  content: string;
}

// =============================================================================
// V4-native write input shapes (camelCase)
//
// These mirror the V4 backend contract exposed by `POST/PUT /v4/quotes`
// (rest_server PR #509). They live alongside the legacy snake_case
// `CreateQuoteInput` / `UpdateQuoteInput` shapes during the transition: the
// legacy types continue to drive the V3-passthrough flow via
// `/v4/legacy/quotes`, while V4-native callers use the shapes below.
//
// Once createQuote / updateQuote / deleteQuote are repointed to V4 (next
// PR), the snake_case input types and `V3QuoteSku` / `V3QuoteSubtotal` can
// be dropped along with `buildQuoteListPatch` in useQuotes.ts.
// =============================================================================

/**
 * Single line item as sent to `POST/PUT /v4/quotes`. Mirrors the PG
 * `quote_line_items` row schema; the backend handles default population
 * (`sortOrder` falls back to array index, `total` to 0, etc.).
 *
 * Numeric fields accept both `string` and `number` — Drizzle's decimal
 * columns ingest either, and the V4 DAO stringifies internally.
 */
export interface QuoteLineItemInput {
  sku?: string | null;
  name?: string | null;
  description?: string | null;
  quantity: string | number;
  unitPrice: string | number;
  discount?: string | number | null;
  total: string | number;
  sortOrder?: number | null;
  itemNumber?: number | null;
  subtotalGroup?: string | null;
  subtotalLabel?: string | null;
  isNote?: boolean;
}

/**
 * Payload accepted by `POST /v4/quotes`. `accountId` is the only required
 * field; everything else is opt-in. `quoteNumber` is intentionally absent
 * — the backend always generates it server-side (collision-safe across
 * Mongo + PG) and ignores any value supplied in the body.
 */
export interface CreateQuoteV4Input {
  accountId: string;

  // Identification / status
  name?: string | null;
  projectName?: string | null;
  description?: string | null;
  status?: QuoteStatus;
  priority?: QuotePriority | null;

  // Dates (calendar days, YYYY-MM-DD — never `new Date()`-parsed on the FE)
  createdDate?: string | null;
  expirationDate?: string | null;
  sentDate?: string | null;
  acceptedDate?: string | null;
  estimatedDelivery?: string | null;
  quoteDate?: string | null;
  nextStepDeadline?: string | null;

  // Financials (string or number — the DAO stringifies for the decimal columns)
  subtotal?: string | number;
  discount?: string | number;
  taxAmount?: string | number;
  total?: string | number;
  currency?: Currency;
  taxCategory?: TaxCategory | null;

  // Managers + auditing (Mongo ObjectIds — staying varchar(24) on the PG row)
  salesManagerId?: string | null;
  contractualManagerId?: string | null;
  createdBy?: string | null;

  // FKs / external links
  subscriptionId?: string | null;
  opportunityId?: string | null;
  mongoQuoteId?: string | null;
  hubspotDealname?: string | null;
  hubspotLink?: string | null;
  gdriveLink?: string | null;

  // Sales/quote metadata
  dealType?: DealType | null;
  quoteVersion?: number | null;
  probability?: number | null;
  language?: Language;
  paymentTerms?: PaymentTerms | null;
  projectNumber?: string | null;
  comment?: string | null;
  internalNotes?: string | null;
  nextStep?: string | null;
  trackingNumbers?: string[] | null;
  pricelist?: string | null;
  ticket?: string | null;

  // PO + addresses
  poNumber?: string | null;
  shippingAddress?: string | null;
  billingSite?: string | null;
  shippingSite?: string | null;
  billingAddress?: string | null;

  // Line items — when omitted, the quote is created with no items.
  lineItems?: QuoteLineItemInput[];
}

/**
 * Payload accepted by `PUT /v4/quotes/:id`. Everything is optional; the
 * backend whitelist refuses identity columns (`id`, `accountId`,
 * `mongoQuoteId`, `quoteNumber`, `createdAt`, `createdBy`, `opportunityId`)
 * and V4-only operations-board state regardless of what's in the body.
 * Use `PATCH /v4/quotes/:id/reassign` to move a quote across accounts.
 *
 * If `lineItems` is provided (any array, including `[]`), all existing
 * line items are replaced atomically. Omit the field to leave them as-is.
 */
export type UpdateQuoteV4Input = Partial<Omit<CreateQuoteV4Input, 'accountId'>>;

/**
 * User for manager dropdowns
 */
export interface User {
  _id: string;
  firstname?: string;
  lastname?: string;
  email?: string;
}

// =============================================================================
// Type Guards
// =============================================================================

export function isPopulatedOrganisation(
  org: string | PopulatedOrganisation | undefined
): org is PopulatedOrganisation {
  return typeof org === 'object' && org !== null && '_id' in org;
}

export function isPopulatedUser(
  user: string | PopulatedUser | undefined
): user is PopulatedUser {
  return typeof user === 'object' && user !== null && '_id' in user;
}

export function isPopulatedContract(
  contract: string | PopulatedContract | undefined
): contract is PopulatedContract {
  return typeof contract === 'object' && contract !== null && '_id' in contract;
}

export function isPopulatedSite(
  site: string | PopulatedSite | undefined
): site is PopulatedSite {
  return typeof site === 'object' && site !== null && '_id' in site;
}

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Get the display name for an account
 */
export function getAccountName(quote: Quote): string {
  return quote.accountName || '-';
}

/**
 * Get the display name for the sales manager
 */
export function getSalesManagerName(quote: Quote): string {
  return quote.salesManagerName || '-';
}

/**
 * Get the display name for the contractual manager
 */
export function getContractualManagerName(quote: Quote): string {
  return quote.contractualManagerName || '-';
}

/**
 * Parse total as number
 */
export function getQuoteTotal(quote: Quote): number {
  return parseFloat(quote.total) || 0;
}

/**
 * Check if quote is expired based on expiration date
 */
export function isQuoteExpired(quote: Quote): boolean {
  if (!quote.expirationDate) return false;
  if (quote.status === 'approved' || quote.status === 'invoiced' || quote.status === 'cancelled') {
    return false;
  }
  return new Date(quote.expirationDate) < new Date();
}

/**
 * Check if quote expiration is approaching (within 7 days)
 */
export function isExpirationApproaching(quote: Quote): boolean {
  if (!quote.expirationDate) return false;
  if (quote.status === 'approved' || quote.status === 'invoiced' || quote.status === 'cancelled') {
    return false;
  }
  const expirationDate = new Date(quote.expirationDate);
  const now = new Date();
  const daysUntilExpiration = Math.ceil((expirationDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  return daysUntilExpiration <= 7 && daysUntilExpiration > 0;
}

/**
 * Calculate tax amount based on category
 */
export function calculateTax(amount: number, taxCategory: TaxCategory | null): number {
  return taxForCategory(amount, taxCategory);
}

/**
 * Calculate total with tax
 */
export function calculateTotalWithTax(
  amount: number,
  taxCategory: TaxCategory | null
): number {
  return amount + calculateTax(amount, taxCategory);
}
